import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { TextRotationBasis } from "building-types/views/Primitive2DOutput";
import type { SheetTextGeometry } from "building-types/views/sheetGeometryTypes";
import type { Table } from "common/data/table";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import type { AABB2 } from "geom/aabb2Types";
import { polyLine2Area } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { MIN_ROOM_SQ_FT_FOR_INTERIOR_DIMENSIONS } from "./floorplanConstants";
import type { GroupOfTaggedPlanGeometries } from "./planGeometrySourceTypes";

export enum CollisionType {
  RoomName = 0,
  DimLine = 1,
  DimLabel = 2,
  DimLabelByString = 3,
  DimAnnotation = 4,
  OpeningTag = 5,
  ExteriorDimensionLabel = 6,
  Casework = 7,
  CaseWorkNote = 8,
  DoorSwingLineWork = 9,
  ElectricLoad = 10,
  Props = 11,
  FramingTag = 12,
}

const collisionMask: { [key in CollisionType]?: number } = {
  [CollisionType.DimLabelByString]: ~(
    (1 << CollisionType.DoorSwingLineWork) |
    (1 << CollisionType.OpeningTag) |
    (1 << CollisionType.Casework)
  ),
};

export interface PlanCollider {
  readonly collisionType: CollisionType;
  readonly rangeAABB: AABB2;
  readonly labelPerimeter: PolyLine2;
  readonly labelAABB: AABB2;
  readonly fullAABB: AABB2;
  readonly label: string;
  readonly labelHeight: number;
  readonly fontSize?: SheetInches;
  readonly labelRotation: number;
  readonly labelRotationBasis?: TextRotationBasis; // NOTE this flags the rotation as being unresolved at this time
  readonly baseLine?: PolyLine2; // NOTE: the line to be measured (used with BY_STRING interior dims)
  readonly dimensionLine?: PolyLine2;
  readonly dimensionLineUnrelaxed?: Readonly<PolyLine2>;
  readonly mass?: number;
  readonly moveOnDimLine?: boolean;
  readonly normalX?: number;
  readonly normalY?: number;

  readonly handle?: DocEntityHandle;
  readonly planTextGeomSource?: SheetTextGeometry & Required<Pick<SheetTextGeometry, "category">>;
  readonly dimPlanGeomSource?: GroupOfTaggedPlanGeometries;

  isCollidingWithObstacle?: boolean;
}

export interface PlanColliderObstacle {
  readonly collisionType: CollisionType;
  readonly perimeter: PolyLine2;
  readonly forceFactor?: number;
}

export function planColliderObstacleNew(
  collisionType: CollisionType,
  perimeter: PolyLine2,
  forceFactor: number = 1,
): PlanColliderObstacle {
  return {
    collisionType,
    perimeter,
    forceFactor,
  };
}

export interface VisitedRoomsTable {
  [roomId: string]: boolean;
}

function getLabelCollisionMask(type: CollisionType): number {
  return collisionMask[type] ?? 0xffffffff;
}

export function checkLabelCollisionMask(srcType: CollisionType, dstType: CollisionType): boolean {
  const mask = getLabelCollisionMask(srcType);

  return ((1 << dstType) & mask) !== 0;
}

export function isValidRoomSet(roomSet: RoomSet, rooms: Table<Room>, visitedRooms: VisitedRoomsTable) {
  if (roomSet.length === 0) {
    return false;
  }

  let totalArea = 0.0;
  for (const roomLike of roomSet) {
    const { id } = roomLike;

    if (visitedRooms[id]) {
      return false;
    }

    totalArea += polyLine2Area(rooms[id].outline);
    visitedRooms[id] = true;
  }

  if (totalArea / 144 <= MIN_ROOM_SQ_FT_FOR_INTERIOR_DIMENSIONS) {
    return false;
  }

  return true;
}

/** Similar to isValidRoomSet, but returns the roomset area if valid and -1 if invalid. */
export function calculateRoomSetArea(roomSet: RoomSet, rooms: Table<Room>, visitedRooms: VisitedRoomsTable): number {
  if (roomSet.length === 0) {
    return -1;
  }

  let totalArea = 0.0;
  for (const roomLike of roomSet) {
    const { id } = roomLike;

    if (visitedRooms[id]) {
      return -1;
    }

    totalArea += polyLine2Area(rooms[id].outline);
    visitedRooms[id] = true;
  }

  return totalArea;
}
